// libs/shared/util/angular-config/src/lib/provide-core.ts

import { DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import {
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  inject,
  isDevMode,
  LOCALE_ID,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import {
  environment,
  ENVIRONMENT_TOKEN,
} from '@nomoh-monorepo/shared-data-access-environment';
import Aura from '@primeng/themes/aura';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { TranslateService } from '@ngx-translate/core';

// ✅ Registrar locales suportados globalmente
registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeFr, 'fr-FR');

// Dicionários completos de tradução para componentes PrimeNG
export const PRIME_NG_PT_BR = {
  startsWith: 'Começa com',
  contains: 'Contém',
  notContains: 'Não contém',
  endsWith: 'Termina com',
  equals: 'Igual a',
  notEquals: 'Diferente de',
  noFilter: 'Sem Filtro',
  lt: 'Menor que',
  lte: 'Menor ou igual a',
  gt: 'Maior que',
  gte: 'Maior ou igual a',
  is: 'É',
  isNot: 'Não é',
  before: 'Antes',
  after: 'Depois',
  dateIs: 'Data é',
  dateIsNot: 'Data não é',
  dateBefore: 'Data é antes de',
  dateAfter: 'Data é depois de',
  clear: 'Limpar',
  apply: 'Aplicar',
  matchAll: 'Corresponder Todos',
  matchAny: 'Corresponder Qualquer',
  addRule: 'Adicionar Regra',
  removeRule: 'Remover Regra',
  accept: 'Sim',
  reject: 'Não',
  choose: 'Escolher',
  upload: 'Enviar',
  cancel: 'Cancelar',
  pending: 'Pendente',
  fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  fileChosenMessage: 'Arquivos',
  noFileChosenMessage: 'Nenhum arquivo selecionado',
  dayNames: [
    'Domingo',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado',
  ],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
  monthNames: [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ],
  monthNamesShort: [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ],
  chooseYear: 'Escolher Ano',
  chooseMonth: 'Escolher Mês',
  chooseDate: 'Escolher Data',
  prevDecade: 'Década Anterior',
  nextDecade: 'Próxima Década',
  prevYear: 'Ano Anterior',
  nextYear: 'Próximo Ano',
  prevMonth: 'Mês Anterior',
  nextMonth: 'Próximo Mês',
  prevHour: 'Hora Anterior',
  nextHour: 'Próxima Hora',
  prevMinute: 'Minuto Anterior',
  nextMinute: 'Próximo Minuto',
  prevSecond: 'Segundo Anterior',
  nextSecond: 'Próximo Segundo',
  am: 'AM',
  pm: 'PM',
  today: 'Hoje',
  weekHeader: 'Sem',
  dateFormat: 'dd/mm/yy',
  firstDayOfWeek: 0,
  weak: 'Fraca',
  medium: 'Média',
  strong: 'Forte',
  passwordPrompt: 'Digite uma senha',
  emptyMessage: 'Nenhum resultado encontrado',
  emptyFilterMessage: 'Nenhum resultado encontrado',
  emptySelectionMessage: 'Nenhum item selecionado',
  emptySearchMessage: 'Nenhum resultado encontrado',
  searchMessage: '{0} resultados disponíveis',
  selectionMessage: '{0} itens selecionados',
  aria: {
    trueLabel: 'Verdadeiro',
    falseLabel: 'Falso',
    nullLabel: 'Não Selecionado',
    star: '1 estrela',
    stars: '{star} estrelas',
    selectAll: 'Todos os itens selecionados',
    unselectAll: 'Todos os itens desmarcados',
    close: 'Fechar',
    previous: 'Anterior',
    next: 'Próximo',
    navigation: 'Navegação',
    scrollTop: 'Voltar ao Topo',
    moveTop: 'Mover para o Topo',
    moveUp: 'Mover para Cima',
    moveDown: 'Mover para Baixo',
    moveBottom: 'Mover para o Final',
    moveToTarget: 'Mover para Destino',
    moveToSource: 'Mover para Origem',
    moveAllToTarget: 'Mover Tudo para Destino',
    moveAllToSource: 'Mover Tudo para Origem',
    pageLabel: '{page}',
    firstPageLabel: 'Primeira Página',
    lastPageLabel: 'Última Página',
    nextPageLabel: 'Próxima Página',
    prevPageLabel: 'Página Anterior',
    rowsPerPageLabel: 'Linhas por página',
    previousPageLabel: 'Página Anterior',
    jumpToPageDropdownLabel: 'Ir para Página',
    jumpToPageInputLabel: 'Ir para Página',
    selectRow: 'Linha Selecionada',
    unselectRow: 'Linha Desmarcada',
    expandRow: 'Expandir Linha',
    collapseRow: 'Recolher Linha',
    showFilterMenu: 'Exibir Menu de Filtro',
    hideFilterMenu: 'Ocultar Menu de Filtro',
    filterOperator: 'Operador de Filtro',
    filterConstraint: 'Restrição de Filtro',
    editRow: 'Editar Linha',
    saveEdit: 'Salvar Edição',
    cancelEdit: 'Cancelar Edição',
    listView: 'Visualização em Lista',
    gridView: 'Visualização em Grade',
    slide: 'Slide',
    slideNumber: '{slideNumber}',
    zoomImage: 'Ampliar Imagem',
    zoomIn: 'Ampliar',
    zoomOut: 'Reduzir',
    rotateRight: 'Girar para a Direita',
    rotateLeft: 'Girar para a Esquerda',
    listLabel: 'Lista de Opções',
    selectColor: 'Selecionar uma cor',
    removeLabel: 'Remover',
    browseFiles: 'Procurar Arquivos',
    maximizeLabel: 'Maximizar',
  },
};

export const PRIME_NG_EN_US = {
  startsWith: 'Starts with',
  contains: 'Contains',
  notContains: 'Not contains',
  endsWith: 'Ends with',
  equals: 'Equals',
  notEquals: 'Not equals',
  noFilter: 'No Filter',
  lt: 'Less than',
  lte: 'Less than or equal to',
  gt: 'Greater than',
  gte: 'Greater than or equal to',
  is: 'Is',
  isNot: 'Is not',
  before: 'Before',
  after: 'After',
  dateIs: 'Date is',
  dateIsNot: 'Date is not',
  dateBefore: 'Date is before',
  dateAfter: 'Date is after',
  clear: 'Clear',
  apply: 'Apply',
  matchAll: 'Match All',
  matchAny: 'Match Any',
  addRule: 'Add Rule',
  removeRule: 'Remove Rule',
  accept: 'Yes',
  reject: 'No',
  choose: 'Choose',
  upload: 'Upload',
  cancel: 'Cancel',
  pending: 'Pending',
  fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  fileChosenMessage: 'Files',
  noFileChosenMessage: 'No file chosen',
  dayNames: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  chooseYear: 'Choose Year',
  chooseMonth: 'Choose Month',
  chooseDate: 'Choose Date',
  prevDecade: 'Previous Decade',
  nextDecade: 'Next Decade',
  prevYear: 'Previous Year',
  nextYear: 'Next Year',
  prevMonth: 'Previous Month',
  nextMonth: 'Next Month',
  prevHour: 'Previous Hour',
  nextHour: 'Next Hour',
  prevMinute: 'Previous Minute',
  nextMinute: 'Next Minute',
  prevSecond: 'Previous Second',
  nextSecond: 'Next Second',
  am: 'AM',
  pm: 'PM',
  today: 'Today',
  weekHeader: 'Wk',
  dateFormat: 'dd/mm/yy',
  firstDayOfWeek: 0,
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
  passwordPrompt: 'Enter a password',
  emptyMessage: 'No results found',
  emptyFilterMessage: 'No results found',
  emptySelectionMessage: 'No item selected',
  emptySearchMessage: 'No results found',
  searchMessage: '{0} results available',
  selectionMessage: '{0} items selected',
  aria: {
    trueLabel: 'True',
    falseLabel: 'False',
    nullLabel: 'Not Selected',
    star: '1 star',
    stars: '{star} stars',
    selectAll: 'All items selected',
    unselectAll: 'All items unselected',
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    navigation: 'Navigation',
    scrollTop: 'Scroll to Top',
    moveTop: 'Move to Top',
    moveUp: 'Move Up',
    moveDown: 'Move Down',
    moveBottom: 'Move to Bottom',
    moveToTarget: 'Move to Target',
    moveToSource: 'Move to Source',
    moveAllToTarget: 'Move All to Target',
    moveAllToSource: 'Move All to Source',
    pageLabel: '{page}',
    firstPageLabel: 'First Page',
    lastPageLabel: 'Last Page',
    nextPageLabel: 'Next Page',
    prevPageLabel: 'Previous Page',
    rowsPerPageLabel: 'Rows per page',
    previousPageLabel: 'Previous Page',
    jumpToPageDropdownLabel: 'Jump to Page Dropdown',
    jumpToPageInputLabel: 'Jump to Page Input',
    selectRow: 'Row Selected',
    unselectRow: 'Row Unselected',
    expandRow: 'Row Expanded',
    collapseRow: 'Row Collapsed',
    showFilterMenu: 'Show Filter Menu',
    hideFilterMenu: 'Hide Filter Menu',
    filterOperator: 'Filter Operator',
    filterConstraint: 'Filter Constraint',
    editRow: 'Row Edit',
    saveEdit: 'Save Edit',
    cancelEdit: 'Cancel Edit',
    listView: 'List View',
    gridView: 'Grid View',
    slide: 'Slide',
    slideNumber: '{slideNumber}',
    zoomImage: 'Zoom Image',
    zoomIn: 'Zoom In',
    zoomOut: 'Zoom Out',
    rotateRight: 'Rotate Right',
    rotateLeft: 'Rotate Left',
    listLabel: 'Option List',
    selectColor: 'Select a color',
    removeLabel: 'Remove',
    browseFiles: 'Browse Files',
    maximizeLabel: 'Maximize',
  },
};

export const PRIME_NG_ES_ES = {
  ...PRIME_NG_PT_BR,
  clear: 'Limpiar',
  apply: 'Aplicar',
  accept: 'Sí',
  reject: 'No',
  choose: 'Elegir',
  upload: 'Subir',
  cancel: 'Cancelar',
  dayNames: [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ],
  dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
  monthNames: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  monthNamesShort: [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ],
  chooseYear: 'Elegir Año',
  chooseMonth: 'Elegir Mes',
  chooseDate: 'Elegir Fecha',
  today: 'Hoy',
  weekHeader: 'Sem',
  dateFormat: 'dd/mm/yy',
  firstDayOfWeek: 1,
  emptyMessage: 'No se encontraron resultados',
  emptyFilterMessage: 'No se encontraron resultados',
  emptySearchMessage: 'No se encontraron resultados',
};

export const PRIME_NG_FR_FR = {
  ...PRIME_NG_PT_BR,
  clear: 'Effacer',
  apply: 'Appliquer',
  accept: 'Oui',
  reject: 'Non',
  choose: 'Choisir',
  upload: 'Téléverser',
  cancel: 'Annuler',
  dayNames: [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ],
  dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
  monthNames: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ],
  monthNamesShort: [
    'Jan',
    'Fév',
    'Mar',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Aoû',
    'Sep',
    'Oct',
    'Nov',
    'Déc',
  ],
  chooseYear: 'Choisir une année',
  chooseMonth: 'Choisir un mois',
  chooseDate: 'Choisir une date',
  today: "Aujourd'hui",
  weekHeader: 'Sem',
  dateFormat: 'dd/mm/yy',
  firstDayOfWeek: 1,
  emptyMessage: 'Aucun résultat trouvé',
  emptyFilterMessage: 'Aucun résultat trouvé',
  emptySearchMessage: 'Aucun resultado trouvé',
};

/**
 * Array de provedores essenciais e comuns para todas as aplicações Angular.
 * Inclui animações, pipe de data, configurações do PrimeNG, internacionalização e services.
 */
export const CORE_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  // ✅ Environment global
  { provide: ENVIRONMENT_TOKEN, useValue: environment },

  provideHttpClient(),
  provideZoneChangeDetection({ eventCoalescing: true }),
  provideAnimationsAsync(),
  DatePipe,
  providePrimeNG({
    theme: {
      preset: Aura,
      options: {
        darkModeSelector: false,
        prefix: 'p',
        cssLayer: {
          name: 'primeng',
          order: 'primeng, app',
        },
      },
    },
    ripple: true,
    translation: PRIME_NG_PT_BR,
  }),
  { provide: LOCALE_ID, useValue: 'pt-BR' },
  { provide: MessageService, useClass: MessageService },
  { provide: ConfirmationService, useClass: ConfirmationService },
  {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () => {
      const primeNg = inject(PrimeNG);
      const translate = inject(TranslateService);

      const updatePrimeNgTranslations = (lang: string) => {
        if (!lang) return;
        const code = lang.split('-')[0].toLowerCase();
        if (code === 'en') {
          primeNg.setTranslation(PRIME_NG_EN_US);
        } else if (code === 'es') {
          primeNg.setTranslation(PRIME_NG_ES_ES);
        } else if (code === 'fr') {
          primeNg.setTranslation(PRIME_NG_FR_FR);
        } else {
          primeNg.setTranslation(PRIME_NG_PT_BR);
        }
      };

      const idiomas = ['pt', 'en', 'es', 'fr'];
      const escolhaManual =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('app-language-manual') === '1';
      const idiomasNavegador =
        typeof navigator === 'undefined'
          ? []
          : navigator.languages?.length
            ? Array.from(navigator.languages)
            : [navigator.language];
      const idiomaNavegador = idiomasNavegador
        .map((item) => item?.split(/[-_]/)[0]?.toLowerCase())
        .find((code) => !!code && idiomas.includes(code));
      const storedLang = escolhaManual
        ? localStorage.getItem('app-language') || translate.currentLang || 'pt'
        : idiomaNavegador || translate.currentLang || 'pt';
      updatePrimeNgTranslations(storedLang);

      translate.onLangChange.subscribe((event) => {
        updatePrimeNgTranslations(event.lang);
      });
    },
  },
  provideServiceWorker('ngsw-worker.js', {
    enabled: !isDevMode(),
    registrationStrategy: 'registerWhenStable:30000',
  }),
];
